if (!customElements.get("wedding-page")) {
  customElements.define(
    "wedding-page",
    class WeddingPage extends HTMLElement {
      static payload(data) {
        const payload = {
          weddingDateConfirmed: data.get("weddingDateConfirmed") === "true",
          nailExperience: Number(data.get("nailExperience")),
          country: "United States",
        };
        for (const key of [
          "weddingPlan",
          "selectedCategory",
          "selectedProduct",
          "firstName",
          "lastName",
          "address",
          "city",
          "state",
          "zipCode",
          "email",
          "phone",
        ]) {
          payload[key] = String(data.get(key) || "").trim();
        }
        if (payload.weddingDateConfirmed)
          payload.weddingDate = data.get("weddingDate");
        if (["true", "false"].includes(data.get("shareWillingness")))
          payload.shareWillingness = data.get("shareWillingness") === "true";
        return payload;
      }

      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;
        this.constructor.configureCartPopup();
        this.suppressGiftPopup();
        this.events = new AbortController();
        const options = { signal: this.events.signal };
        this.form = this.querySelector("[data-wedding-form]");
        // Run native field validation in page order alongside the custom look choices.
        this.form.noValidate = true;
        this.form.elements.address.addEventListener(
          "focus", () => this.bindShippingAutocomplete(), options,
        );
        this.form.elements.address.addEventListener("keydown", (event) => {
          // Enter selects a Places suggestion without submitting the application.
          if (event.key === "Enter") event.preventDefault();
        }, options);
        this.messages = JSON.parse(
          this.querySelector("[data-messages]").textContent,
        );
        this.addEventListener("click", (event) => this.onClick(event), options);
        this.addEventListener(
          "keydown",
          (event) => this.onKeydown(event),
          options,
        );
        this.addEventListener(
          "change",
          (event) => {
            if (
              event.target.name === "dateUnknown" ||
              event.target.matches("[data-date-part]")
            )
              this.updateDate();
          },
          options,
        );
        this.form.addEventListener(
          "submit",
          (event) => this.submit(event),
          options,
        );
        this.form.addEventListener(
          "input",
          (event) => event.target.setCustomValidity?.(""),
          options,
        );
        document.addEventListener(
          "shopify:block:select",
          (event) => {
            if (this.querySelector(`[data-panel="${event.detail.blockId}"]`))
              this.selectStyle(event.detail.blockId);
          },
          options,
        );
        this.querySelectorAll("[data-products]").forEach((track) => {
          for (const event of ['pointerdown', 'wheel']) {
            track.addEventListener(event, () => this.cancelProductMotion(), { ...options, passive: true });
          }
          track.addEventListener("scroll", () => this.updateCarousel(), {
            ...options,
            passive: true,
          });
        });
        this.querySelectorAll('.wedding__thumbnails').forEach((track) => {
          track.addEventListener('scroll', () => {
            clearTimeout(track.lookScrollTimer);
            track.lookScrollTimer = setTimeout(() => {
              if (track.closest('[data-panel]').hidden) return;
              const center = track.getBoundingClientRect().left + track.clientWidth / 2;
              const looks = Array.from(track.querySelectorAll('[data-look]'));
              if (looks.length < 3) return;
              const distance = button => Math.abs(button.getBoundingClientRect().left + button.offsetWidth / 2 - center);
              const nearest = looks.reduce((best, button) => distance(button) < distance(best) ? button : best);
              if (nearest === track.positionedLook) return;
              this.selectLook(nearest);
              this.setProductLook(track.closest('[data-panel]').dataset.panel, nearest.dataset.lookIndex);
            }, 150);
          }, { ...options, passive: true });
        });
        window.addEventListener(
          "resize",
          () => {
            this.cancelProductMotion();
            this.selectMobileDefaultStyle();
            this.centerProduct(this.activeProductIndex || 0, false);
            this.centerLook();
          },
          options,
        );
        this.initDatePickers(options);
        this.updateDate();
        this.selectMobileDefaultStyle(true);
        this.updateCartButton();
        this.showInitialProduct();
        this.centerLook();
      }

      static loadShippingPlaces() {
        if (window.google?.maps?.places?.Autocomplete) return Promise.resolve();
        if (this.placesLoading) return this.placesLoading;
        this.placesLoading = new Promise((resolve, reject) => {
          let script = document.querySelector("script[data-rbf-google-maps]");
          const existing = Boolean(script);
          if (!script) {
            script = document.createElement("script");
            // Same public browser key and Places service as the recycling form.
            script.src = "https://maps.googleapis.com/maps/api/js?key=AIzaSyDdfF9e7lZNUT_9QpGLYw2Dv1Nom_nD2C4&libraries=places&v=weekly&language=en";
            script.async = true;
            script.setAttribute("data-rbf-google-maps", "");
          }
          const timeout = setTimeout(() => fail(), 15000);
          const cleanup = () => {
            clearTimeout(timeout);
            script.removeEventListener("load", loaded);
            script.removeEventListener("error", fail);
          };
          const loaded = () => {
            cleanup();
            script.setAttribute("data-loaded", "true");
            if (window.google?.maps?.places?.Autocomplete) resolve();
            else reject(new Error("Places unavailable"));
          };
          const fail = () => {
            cleanup();
            if (!existing) script.remove();
            reject(new Error("Places unavailable"));
          };
          script.addEventListener("load", loaded, { once: true });
          script.addEventListener("error", fail, { once: true });
          if (!existing) document.head.appendChild(script);
          else if (script.getAttribute("data-loaded") === "true") loaded();
        }).catch((error) => {
          this.placesLoading = null;
          throw error;
        });
        return this.placesLoading;
      }

      async bindShippingAutocomplete() {
        if (this.addressAutocomplete || this.addressLoading) return;
        this.addressLoading = true;
        const signal = this.events.signal;
        try {
          await this.constructor.loadShippingPlaces();
          if (signal.aborted) return;
          this.addressAutocomplete = new window.google.maps.places.Autocomplete(
            this.form.elements.address,
            {
              componentRestrictions: { country: ["us"] },
              fields: ["address_components"],
              types: ["address"],
            },
          );
          this.addressListener = this.addressAutocomplete.addListener(
            "place_changed", () => this.fillShippingAddress(this.addressAutocomplete.getPlace()),
          );
        } catch {
          // The existing editable fields remain usable if Places cannot load.
        } finally {
          this.addressLoading = false;
        }
      }

      fillShippingAddress(place) {
        if (!place?.address_components?.length) return;
        const components = place.address_components;
        const part = (type, short = false) => {
          const component = components.find(item => item.types.includes(type));
          return component?.[short ? "short_name" : "long_name"] || "";
        };
        const address = [part("street_number"), part("route", true)].filter(Boolean).join(" ");
        if (!address) return;
        const zip = part("postal_code");
        const suffix = part("postal_code_suffix");
        const values = {
          address,
          city: part("locality") || part("postal_town") || part("sublocality_level_1") || part("sublocality"),
          state: part("administrative_area_level_1", true),
          zipCode: zip && suffix ? `${zip}-${suffix}` : zip,
        };
        for (const [name, value] of Object.entries(values)) {
          const field = this.form.elements[name];
          field.value = value;
          field.setCustomValidity("");
          // Places already owns the address selection event. A synthetic input
          // here starts another prediction request and reopens its dropdown.
          if (name === "address") continue;
          field.dispatchEvent(new Event("input", { bubbles: true }));
          field.dispatchEvent(new Event("change", { bubbles: true }));
        }
      }

      initDatePickers(events) {
        this.datePickers = Array.from(this.querySelectorAll('[data-date-part]'), select => {
          const field = select.closest('.wedding__date-part');
          const label = field.querySelector('label').textContent.trim();
          const trigger = document.createElement('button');
          trigger.type = 'button';
          trigger.className = 'wedding__date-trigger';
          trigger.setAttribute('role', 'combobox');
          trigger.setAttribute('aria-label', label);
          trigger.setAttribute('aria-haspopup', 'listbox');
          trigger.setAttribute('aria-expanded', 'false');
          const list = document.createElement('div');
          list.className = 'wedding__date-options';
          list.id = `${select.id}-options`;
          list.hidden = true;
          list.setAttribute('role', 'listbox');
          list.setAttribute('aria-label', label);
          trigger.setAttribute('aria-controls', list.id);
          const monthFormat = new Intl.DateTimeFormat(document.documentElement.lang || 'en', { month: 'long', timeZone: 'UTC' });
          const choices = Array.from(select.options, (option, index) => {
            if (select.dataset.datePart === 'month') option.textContent = monthFormat.format(new Date(Date.UTC(2024, Number(option.value) - 1, 1)));
            const choice = document.createElement('div');
            choice.id = `${select.id}-option-${index}`;
            choice.setAttribute('role', 'option');
            choice.dataset.value = option.value;
            choice.textContent = option.textContent;
            list.append(choice);
            return choice;
          });
          let active = 0;
          const close = () => {
            list.hidden = true;
            trigger.setAttribute('aria-expanded', 'false');
            trigger.removeAttribute('aria-activedescendant');
            field.classList.remove('is-open');
          };
          const highlight = index => {
            active = index;
            choices.forEach((choice, i) => choice.classList.toggle('is-focused', i === active));
            trigger.setAttribute('aria-activedescendant', choices[active].id);
            list.scrollTop = choices[active].offsetTop - list.clientHeight / 2 + choices[active].offsetHeight / 2;
          };
          const open = () => {
            if (select.disabled) return;
            this.datePickers.forEach(picker => picker.close());
            list.hidden = false;
            field.classList.add('is-open');
            trigger.setAttribute('aria-expanded', 'true');
            highlight(Math.max(0, select.selectedIndex));
          };
          const commit = index => {
            if (select.options[index].disabled) return;
            select.value = select.options[index].value;
            trigger.removeAttribute('aria-invalid');
            select.dispatchEvent(new Event('change', { bubbles: true }));
            close();
            trigger.focus({ preventScroll: true });
          };
          trigger.addEventListener('click', () => list.hidden ? open() : close(), events);
          trigger.addEventListener('keydown', event => {
            if (event.key === 'Escape' || event.key === 'Tab') {
              close();
              if (event.key === 'Escape') event.preventDefault();
              return;
            }
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              if (list.hidden) open(); else commit(active);
              return;
            }
            if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
            event.preventDefault();
            if (list.hidden) { open(); return; }
            const enabled = choices.map((_, i) => i).filter(i => !select.options[i].disabled);
            const position = enabled.indexOf(active);
            const next = event.key === 'Home' ? 0 : event.key === 'End' ? enabled.length - 1 : Math.max(0, Math.min(enabled.length - 1, position + (event.key === 'ArrowDown' ? 1 : -1)));
            highlight(enabled[next]);
          }, events);
          list.addEventListener('pointerdown', event => event.preventDefault(), events);
          list.addEventListener('click', event => {
            const index = choices.indexOf(event.target.closest('[role="option"]'));
            if (index >= 0) commit(index);
          }, events);
          field.addEventListener('focusout', event => {
            if (!field.contains(event.relatedTarget)) close();
          }, events);
          select.addEventListener('invalid', event => {
            event.preventDefault();
            trigger.setAttribute('aria-invalid', 'true');
            if (!this.querySelector('.wedding__date-part.is-open')) { trigger.focus(); open(); }
          }, events);
          field.append(trigger, list);
          field.classList.add('is-enhanced');
          select.tabIndex = -1;
          select.setAttribute('aria-hidden', 'true');
          return { close, sync: () => {
            trigger.textContent = select.selectedOptions[0]?.textContent || label;
            trigger.disabled = select.disabled;
            choices.forEach((choice, i) => {
              choice.setAttribute('aria-selected', String(select.options[i].selected));
              choice.setAttribute('aria-disabled', String(select.options[i].disabled));
            });
            if (select.disabled) close();
          } };
        });
        document.addEventListener('pointerdown', event => {
          if (!event.target.closest('.wedding__date-part')) this.datePickers.forEach(picker => picker.close());
        }, events);
      }

      updateDate() {
        const unknown = this.form.elements.dateUnknown.checked;
        const year = this.querySelector('[data-date-part="year"]');
        const month = this.querySelector('[data-date-part="month"]');
        const day = this.querySelector('[data-date-part="day"]');
        const days = month.value
          ? new Date(
              Number(year.value) || 2000,
              Number(month.value),
              0,
            ).getDate()
          : 31;
        Array.from(day.options).forEach((option) => {
          option.disabled = Number(option.value) > days;
        });
        if (Number(day.value) > days) day.value = "";
        [year, month, day].forEach((select) => {
          select.disabled = unknown;
          select.required = !unknown;
        });
        this.form.elements.weddingDateConfirmed.value = String(!unknown);
        this.form.elements.weddingDate.disabled = unknown;
        this.form.elements.weddingDate.value =
          !unknown && year.value && month.value && day.value
            ? `${year.value}-${month.value.padStart(2, "0")}-${day.value.padStart(2, "0")}`
            : "";
        this.datePickers?.forEach(picker => picker.sync());
      }

      activeTrack() {
        return this.querySelector("[data-products]:not([hidden])");
      }

      setProductLook(id, index) {
        this.cancelProductMotion();
        this.querySelectorAll("[data-products]").forEach((track) => {
          track.hidden = track.dataset.products !== id || track.dataset.productsLook !== String(index);
        });
        this.updateCartButton();
        this.showInitialProduct();
      }

      showInitialProduct() {
        const cards = Array.from(this.activeTrack()?.querySelectorAll('[data-product-card]') || []);
        this.activeProductIndex = Math.max(0, cards.findIndex(card => card.querySelector('.wedding__trial-badge')));
        this.centerProduct(this.activeProductIndex, false);
      }

      selectLook(look) {
        if (!look) return;
        const panel = look.closest("[data-panel]");
        const tab = this.querySelector(`[data-style="${panel.dataset.panel}"]`);
        if (tab?.getAttribute("aria-selected") !== "true") this.selectStyle(panel.dataset.panel);
        const image = panel.querySelector(".wedding__look-image");
        if (image && look.dataset.look) {
          image.removeAttribute("srcset");
          image.src = look.dataset.look;
          image.alt = look.dataset.alt;
          panel.querySelector("[data-look-source]").srcset =
            look.dataset.lookMobile || look.dataset.look;
        }
        panel.querySelector("[data-look-picture]").hidden = !look.dataset.look;
        panel.querySelector("[data-look-placeholder]").hidden = Boolean(look.dataset.look);
        panel.querySelectorAll("[data-look]").forEach((button) =>
          button.setAttribute("aria-pressed", String(button === look)),
        );
        this.centerLook(look);
        this.syncTrialSelection(panel.dataset.panel, look);
      }

      syncTrialSelection(styleId, look) {
        const tab = this.querySelector(`[data-style="${styleId}"]`);
        const trials = look
          ? this.querySelectorAll(`[data-products="${styleId}"][data-products-look="${look.dataset.lookIndex}"] [data-trial-sku]`)
          : [];
        this.form.elements.selectedCategory.value = tab?.textContent.trim() || '';
        this.form.elements.selectedProduct.value = trials.length === 1 ? trials[0].dataset.trialSku.trim() : '';
        this.querySelectorAll('[data-selection-error]').forEach(error => {
          error.hidden = true;
          error.textContent = '';
        });
        this.querySelectorAll('[data-style][aria-invalid], [data-look][aria-invalid]').forEach(button => button.removeAttribute('aria-invalid'));
      }

      reportTrialSelection() {
        const categoryMissing = !this.form.elements.selectedCategory.value.trim();
        if (!categoryMissing && this.form.elements.selectedProduct.value.trim()) return true;
        const panel = this.querySelector('[data-panel]:not([hidden])');
        const look = panel?.querySelector('[data-look][aria-pressed="true"]');
        const error = categoryMissing
          ? this.querySelector('[data-selection-error="category"]')
          : panel.querySelector('[data-selection-error="product"]');
        error.textContent = categoryMissing ? this.messages.category_required
          : look ? this.messages.trial_unavailable : this.messages.product_required;
        error.hidden = false;
        const control = categoryMissing
          ? this.querySelector('[data-style][aria-selected="true"]') || this.querySelector('[data-style]')
          : look || panel.querySelector('[data-look]');
        control?.setAttribute('aria-invalid', 'true');
        control?.focus({ preventScroll: true });
        error.scrollIntoView({ block: 'center', behavior: 'auto' });
        return false;
      }

      centerLook(look = this.querySelector('[data-panel]:not([hidden]) [data-look][aria-pressed="true"]')) {
        const panel = this.querySelector('[data-panel]:not([hidden])');
        look ||= panel?.querySelector('[data-look-index="2"]') || panel?.querySelector('[data-look]');
        if (!look) return;
        const track = look.closest('.wedding__thumbnails');
        clearTimeout(track.lookScrollTimer);
        const looks = Array.from(track.querySelectorAll('[data-look]'));
        track.positionedLook = look;
        track.classList.toggle('has-two-looks', looks.length === 2);
        looks.forEach((button) => {
          button.classList.toggle('is-centered', button.getAttribute('aria-pressed') === 'true');
        });
        if (look.getAttribute('aria-pressed') !== 'true' && matchMedia('(max-width: 768px)').matches) {
          track.scrollTo({
            left: looks.length === 2 ? 0 : track.scrollLeft + look.getBoundingClientRect().left - track.getBoundingClientRect().left - (track.clientWidth - look.offsetWidth) / 2,
            behavior: 'instant',
          });
        }
      }

      cancelProductMotion() {
        this.productMoves = [];
        const motion = this.productMotion;
        if (!motion) return;
        this.productMotion = null;
        cancelAnimationFrame(motion.frame);
        motion.animation?.cancel();
        motion.resolve?.();
        motion.track.classList.remove('is-product-moving');
        this.updateCarousel();
      }

      async moveProduct(direction) {
        this.productMoves ||= [];
        this.productMoves.push(direction);
        if (this.productMotion) return;
        while (this.productMoves.length) {
          const step = this.productMoves.shift();
          const track = this.activeTrack();
          const cards = track ? Array.from(track.querySelectorAll('[data-product-card]')) : [];
          if (cards.length < 2) return;
          const index = this.activeProductIndex + step;
          const next = (index + cards.length) % cards.length;
          if (cards.length === 2 || matchMedia('(prefers-reduced-motion: reduce)').matches) {
            this.centerProduct(next, false);
            continue;
          }
          const motion = { track };
          this.productMotion = motion;
          track.classList.add('is-product-moving');
          try {
            if (index < 0 || index >= cards.length) {
              motion.animation = track.animate([
                { opacity: 1, transform: 'translateX(0)' },
                { opacity: 0, transform: `translateX(${-step * 16}px)` },
              ], { duration: 120, easing: 'ease-in', fill: 'forwards' });
              await motion.animation.finished;
              if (this.productMotion !== motion) return;
              this.centerProduct(next, false);
              cards.forEach((card, i) => card.classList.toggle('is-active', i === next));
              motion.animation.cancel();
              motion.animation = track.animate([
                { opacity: 0, transform: `translateX(${step * 16}px)` },
                { opacity: 1, transform: 'translateX(0)' },
              ], { duration: 240, easing: 'cubic-bezier(.22, 1, .36, 1)' });
              await motion.animation.finished;
            } else {
              const target = cards[next];
              const start = track.scrollLeft;
              const left = start + target.getBoundingClientRect().left - track.getBoundingClientRect().left -
                (track.clientWidth - target.offsetWidth) / 2;
              const destination = Math.max(0, Math.min(left, track.scrollWidth - track.clientWidth));
              cards.forEach((card, i) => card.classList.toggle('is-active', i === next));
              await new Promise(resolve => {
                motion.resolve = resolve;
                const started = performance.now();
                const tick = now => {
                  if (this.productMotion !== motion) return resolve();
                  const progress = Math.min(1, (now - started) / 420);
                  track.scrollLeft = start + (destination - start) * (1 - Math.pow(1 - progress, 3));
                  if (progress < 1) motion.frame = requestAnimationFrame(tick);
                  else resolve();
                };
                motion.frame = requestAnimationFrame(tick);
              });
            }
            if (this.productMotion !== motion) return;
          } catch (error) {
            if (error.name !== 'AbortError') throw error;
            return;
          } finally {
            if (this.productMotion === motion) {
              motion.animation?.cancel();
              track.classList.remove('is-product-moving');
              this.productMotion = null;
              this.centerProduct(next, false);
            }
          }
          if (this.activeTrack() !== track) return;
        }
      }

      centerProduct(index, smooth = true) {
        const track = this.activeTrack();
        const cards = track
          ? Array.from(track.querySelectorAll("[data-product-card]"))
          : [];
        if (!cards.length) {
          this.updateCarousel();
          return;
        }
        const targetIndex = ((index % cards.length) + cards.length) % cards.length;
        if (cards.length === 2) {
          this.activeProductIndex = targetIndex;
          track.scrollTo({ left: 0, behavior: 'instant' });
          this.updateCarousel();
          return;
        }
        const target = cards[targetIndex];
        const wraps = index < 0 || index >= cards.length;
        const left =
          track.scrollLeft +
          target.getBoundingClientRect().left -
          track.getBoundingClientRect().left -
          (track.clientWidth - target.offsetWidth) / 2;
        track.scrollTo({
          left,
          behavior:
            smooth && !wraps && !matchMedia("(prefers-reduced-motion: reduce)").matches
              ? "smooth"
              : "instant",
        });
        this.updateCarousel();
      }

      updateCarousel() {
        if (this.productMotion) return;
        const track = this.activeTrack();
        const cards = track
          ? Array.from(track.querySelectorAll("[data-product-card]"))
          : [];
        let index = 0;
        if (cards.length === 2) {
          index = Math.max(0, Math.min(this.activeProductIndex || 0, 1));
        } else if (track && cards.length) {
          const center =
            track.getBoundingClientRect().left + track.clientWidth / 2;
          index = cards.reduce((nearest, card, i) => {
            const distance = (node) =>
              Math.abs(
                node.getBoundingClientRect().left +
                  node.offsetWidth / 2 -
                  center,
              );
            return distance(card) < distance(cards[nearest]) ? i : nearest;
          }, 0);
        }
        cards.forEach((card, i) =>
          card.classList.toggle("is-active", i === index),
        );
        this.activeProductIndex = index;
        this.querySelector("[data-product-prev]").disabled = cards.length <= 1;
        this.querySelector("[data-product-next]").disabled = cards.length <= 1;
      }

      disconnectedCallback() {
        this.giftPopupObserver?.disconnect();
        this.cancelProductMotion();
        this.querySelectorAll('.wedding__thumbnails').forEach(track => clearTimeout(track.lookScrollTimer));
        this.addressListener?.remove();
        this.addressAutocomplete?.unbindAll();
        this.addressAutocomplete = null;
        this.events?.abort();
        this.request?.abort();
        this.initialized = false;
      }

      selectMobileDefaultStyle(initial = false) {
        if (!matchMedia('(max-width: 768px)').matches) return;
        const selected = this.querySelector('[data-style][aria-selected="true"]');
        if (selected && !initial) return;
        const first = selected || this.querySelector('[data-style]');
        if (!first) return;
        if (!selected) this.selectStyle(first.dataset.style);
        const panel = this.querySelector(`[data-panel="${first.dataset.style}"]`);
        this.centerLook(panel.querySelector('[data-look-index="2"]') || panel.querySelector('[data-look]'));
      }

      selectStyle(id, selectedByUser = false) {
        const intro = this.querySelector('[data-style-intro-copy]:not([hidden])');
        if (intro) {
          intro.hidden = true;
          const panel = intro.closest('[data-panel]');
          panel.querySelector('[data-style-copy]').hidden = false;
          const scene = panel.querySelector('[data-scene]');
          const firstLook = panel.querySelector('[data-look]');
          const url = scene.dataset.scene || firstLook?.dataset.look;
          if (url) {
            const image = panel.querySelector('.wedding__look-image');
            image.removeAttribute('srcset');
            image.src = url;
            image.alt = scene.dataset.sceneAlt || firstLook?.dataset.alt || '';
            panel.querySelector('[data-look-source]').srcset = url;
          }
        }
        this.querySelectorAll("[data-style]").forEach((tab) => {
          const selected = tab.dataset.style === id;
          tab.setAttribute("aria-selected", String(selected));
          tab.tabIndex = selected ? 0 : -1;
        });
        this.querySelectorAll("[data-panel]").forEach((panel) => {
          panel.hidden = panel.dataset.panel !== id;
        });
        const selectedLook = this.querySelector(
          `[data-panel="${id}"] [data-look][aria-pressed="true"]`,
        );
        this.setProductLook(id, selectedLook?.dataset.lookIndex || 1);
        this.centerLook(selectedLook);
        if (selectedByUser || this.form.elements.selectedCategory.value)
          this.syncTrialSelection(id, selectedLook);
      }

      updateCartButton() {
        this.querySelector("[data-add-all]").disabled =
          Boolean(this.cartBusy) ||
          !this.querySelector(
            "[data-products]:not([hidden]) [data-variant]:not(:disabled)",
          );
      }

      onKeydown(event) {
        const tab = event.target.closest("[data-style]");
        if (
          !tab ||
          ![
            "ArrowLeft",
            "ArrowRight",
            "ArrowUp",
            "ArrowDown",
            "Home",
            "End",
          ].includes(event.key)
        )
          return;
        event.preventDefault();
        const tabs = Array.from(this.querySelectorAll("[data-style]"));
        if (matchMedia('(max-width: 768px)').matches)
          tabs.sort((a, b) => a.getBoundingClientRect().top - b.getBoundingClientRect().top);
        let index = tabs.indexOf(tab);
        if (event.key === "Home") index = 0;
        else if (event.key === "End") index = tabs.length - 1;
        else
          index =
            (index +
              (["ArrowRight", "ArrowDown"].includes(event.key) ? 1 : -1) +
              tabs.length) %
            tabs.length;
        this.selectStyle(tabs[index].dataset.style, true);
        tabs[index].focus();
      }

      onClick(event) {
        const dialog = this.querySelector("[data-success]");
        if (event.target.closest("[data-close-success]")) dialog.close();
        if (event.target === dialog) {
          const bounds = dialog.getBoundingClientRect();
          if (event.clientX < bounds.left || event.clientX > bounds.right ||
              event.clientY < bounds.top || event.clientY > bounds.bottom) dialog.close();
        }
        const tab = event.target.closest("[data-style]");
        if (tab) this.selectStyle(tab.dataset.style, true);
        if (event.target.closest("[data-product-prev]"))
          this.moveProduct(-1);
        if (event.target.closest("[data-product-next]"))
          this.moveProduct(1);
        const look = event.target.closest("[data-look]");
        if (look) {
          const panel = look.closest("[data-panel]");
          this.selectLook(look);
          this.setProductLook(panel.dataset.panel, look.dataset.lookIndex);
        }
        if (event.target.closest("[data-continue]")) {
          const controls = Array.from(
            this.querySelectorAll(
              ".wedding__quiz input, .wedding__quiz select, .wedding__quiz textarea",
            ),
          );
          for (const control of controls) {
            if (control.tagName === "TEXTAREA")
              control.value = control.value.trim();
            if (!control.reportValidity()) return;
          }
          this.querySelector(".wedding__styles").scrollIntoView({
            behavior: matchMedia("(prefers-reduced-motion: reduce)").matches
              ? "auto"
              : "smooth",
          });
          this.querySelector("[data-style]")?.focus({ preventScroll: true });
        }
        if (event.target.closest("[data-add-all]")) this.addAll();
        if (event.target.closest("[data-copy]")) this.copyCode();
      }

      suppressGiftPopup() {
        // Cart changes and late app initialization can bypass the request hook.
        // Close the native dialog as well as hiding it so the page is not inert.
        const closeGiftPopup = (mutations) => {
          if (mutations && !mutations.some(mutation =>
            mutation.type === 'childList' || mutation.attributeName === 'open' || mutation.target === document.body
          )) return;
          const dialog = document.getElementById('lb-dealeasy-dialog');
          if (!dialog?.querySelector('lb-gift-product-picker')) return;
          if (dialog.open) dialog.close();
          // Dealeasy's scroll lock outlives native close(), and can be added
          // again when the app hydrates after we have closed the dialog.
          if (document.body.classList.contains('lb-dealeasy-modal')) {
            document.body.classList.remove('lb-dealeasy-modal');
          }
        };
        this.giftPopupObserver = new MutationObserver(closeGiftPopup);
        this.giftPopupObserver.observe(document.body, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['open', 'class'],
        });
        closeGiftPopup();
      }

      static configureCartPopup() {
        if (this.cartPopupConfigured) return;
        this.cartPopupConfigured = true;
        const previous = window.dealeasySourcesToIgnore;
        window.dealeasySourcesToIgnore = function (url) {
          if (document.querySelector('wedding-page') && url.searchParams.get('src') === 'wedding-add-all') return true;
          return typeof previous === 'function' ? previous.call(this, url) : false;
        };
      }

      async refreshCart(result) {
        const pubsub = window.FoxTheme?.pubsub;
        if (!pubsub) return;
        try {
          const response = await fetch(`${this.dataset.root}cart.js`, {
            headers: { Accept: 'application/json' },
          });
          if (!response.ok) throw new Error('Unable to refresh cart');
          const cart = await response.json();
          cart.sections = result.sections || {};
          pubsub.publish(pubsub.PUB_SUB_EVENTS.cartUpdate, { cart });
          document.querySelector('cart-drawer')?.show(this.querySelector('[data-add-all]'));
        } catch (error) {
          // The products were already added. Retry the drawer UI without
          // reporting an add failure that could prompt a duplicate purchase.
          console.error('Wedding cart refresh:', error);
          document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { open: true } }));
        }
      }

      async addAll() {
        if (this.cartBusy) return;
        const variants = Array.from(
          this.querySelectorAll(
            "[data-products]:not([hidden]) [data-variant]:not(:disabled)",
          ),
        );
        const items = variants
          .filter((select) => select.value)
          .map((select) => ({ id: select.value, quantity: 1 }));
        if (!items.length) return;
        this.cartBusy = true;
        this.updateCartButton();
        const status = this.querySelector("[data-cart-status]");
        status.dataset.state = 'loading';
        status.textContent = this.messages.adding;
        try {
          const sections = [];
          document.documentElement.dispatchEvent(new CustomEvent('cart:grouped-sections', {
            bubbles: true, detail: { sections },
          }));
          const response = await fetch(`${this.dataset.root}cart/add.js?src=wedding-add-all`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
            },
            body: JSON.stringify({
              items,
              ...(sections.length ? { sections, sections_url: window.location.pathname } : {}),
            }),
          });
          const result = await response.json();
          if (!response.ok)
            throw new Error(
              result.description || result.message || this.messages.error,
            );
          await this.refreshCart(result);
          status.dataset.state = 'success';
          status.textContent = '';
          window.publicComponents?.messageOpen({
            content: this.messages.added,
            type: 'success',
            duration: 3000,
          });
          await new Promise(resolve => setTimeout(resolve, 3000));
          status.textContent = '';
          delete status.dataset.state;
        } catch (error) {
          status.dataset.state = 'error';
          status.textContent = error.message || this.messages.error;
        } finally {
          this.cartBusy = false;
          this.updateCartButton();
        }
      }

      static async subscribeEmail(email) {
        const request = new AbortController();
        const timeout = setTimeout(() => request.abort(), 10000);
        try {
          const response = await fetch(
            "https://informationcollection.benyuan1.com/api/activityUser/save",
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                email,
                activityName: "Wedding Nail Trial",
                activityTagList: ["BridalNailBar"],
              }),
              signal: request.signal,
              keepalive: true,
            },
          );
          if (!response.ok) throw new Error("Activity subscription failed");
        } finally {
          clearTimeout(timeout);
        }
      }

      async submit(event) {
        event.preventDefault();
        if (this.submitting || this.submitted) return;
        for (const control of this.form.querySelectorAll(
          'input[type="text"], input[type="email"], input[type="tel"], textarea',
        ))
          control.value = control.value.trim();
        for (const control of this.form.querySelectorAll(
          '.wedding__quiz input, .wedding__quiz select, .wedding__quiz textarea',
        ))
          if (!control.reportValidity()) return;
        if (!this.reportTrialSelection()) return;
        if (!this.form.reportValidity()) return;
        const submittedPayload = this.constructor.payload(new FormData(this.form));
        const button = this.querySelector("[data-submit]");
        const status = this.querySelector("[data-form-status]");
        this.submitting = true;
        button.disabled = true;
        this.form.setAttribute("aria-busy", "true");
        status.textContent = this.messages.sending;
        this.request = new AbortController();
        const timeout = setTimeout(() => this.request.abort(), 20000);
        try {
          const response = await fetch(this.dataset.endpoint, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
            },
            body: JSON.stringify(submittedPayload),
            signal: this.request.signal,
          });
          const result = await response.json();
          if (response.status !== 200 || result.status !== "success")
            throw new Error(result.message || this.messages.error);
          this.submitted = true;
          // Subscribe the saved email, independently of the application result UI.
          void this.constructor.subscribeEmail(submittedPayload.email).catch(() => {
            console.warn("Wedding application saved; activity subscription failed.");
          });
          status.textContent = "";
          const success = this.querySelector("[data-success]");
          this.querySelector("[data-discount-code]").textContent =
            result.data?.discountCode || "";
          this.querySelector("[data-discount-info]").textContent =
            result.data?.discountInfo || "";
          const discount = String(result.data?.discountInfo || '').trim();
          const percentage = discount.match(/^(\d+(?:\.\d+)?)\s*%\s*(?:OFF)?$/i);
          this.querySelector('[data-discount-heading]').hidden = !discount;
          this.querySelector('[data-discount-description]').hidden = !percentage;
          this.querySelector('[data-discount-unit]').textContent = percentage ? '%' : '';
          this.querySelector('[data-discount-off]').hidden = !percentage;
          this.querySelector('[data-discount-percent]').textContent = percentage ? `${percentage[1]}%` : '';
          if (percentage) this.querySelector('[data-discount-info]').textContent = percentage[1];
          this.querySelector("[data-copy]").hidden = !result.data?.discountCode;
          success.hidden = false;
          success.showModal();
        } catch (error) {
          status.textContent =
            error.name === "AbortError" || error instanceof TypeError
              ? this.messages.error
              : error.message || this.messages.error;
        } finally {
          clearTimeout(timeout);
          this.submitting = false;
          button.disabled = Boolean(this.submitted);
          this.form.removeAttribute("aria-busy");
        }
      }

      async copyCode() {
        const status = this.querySelector("[data-copy-status]");
        try {
          await navigator.clipboard.writeText(
            this.querySelector("[data-discount-code]").textContent,
          );
          status.textContent = this.messages.copied;
          status.classList.remove('is-error');
          const button = this.querySelector('[data-copy]');
          button.textContent = button.dataset.copiedLabel;
        } catch {
          status.textContent = this.messages.copy_error;
          status.classList.add('is-error');
        }
      }
    },
  );
}
